import { useLayoutEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ErrorState,
  Input,
  Label,
  Loading,
  RequiredMark,
  Textarea,
} from '@attendance/ui';
import { friendlyMessage } from '../../lib/errors.js';
import type { CompanyDraft, CompanyFields } from './useCompanyDraft.js';

// Only fields that Company info actually owns are editable here. Timezone and
// primary colour used to be duplicated on this page AND under Settings, giving
// two sources of truth that could drift apart. They are now read-only here and
// deferred to their canonical screen (see CANONICAL_FIELDS below).
// emiratesId / email / website were editable here but no code reads company.*
// for them (letters/payslips use the EMPLOYEE emiratesId, not the company's),
// so their controls were removed rather than left as fields that save but do
// nothing. The values remain in the persisted payload, just un-editable.
//
// CURRENCY IS EDITABLE — a correction, not a change of mind. It was a read-only
// mirror pointing at "Settings → General", a group that does not exist and a
// `currency` field no group holds, so the value was editable nowhere while it
// prints beside every payslip and letter figure (`payslip.service.ts`,
// `letter.service.ts`). This page holds the payload, so this page owns it.
//
// THE READING ORDER IS THE FILLING ORDER, and the two-column pairing follows
// it: who you are (name · legal name), what you are registered and reachable as
// (TRN · phone), where you are and what you pay in (address · currency), then
// the value the pay rules own (timezone). Nothing here is a question that can
// only be answered after a later one.
const TEXT_FIELDS: Array<{
  key: keyof CompanyFields;
  labelKey: string;
  labelFallback: string;
}> = [
  { key: 'name', labelKey: 'settings.companyInfo.field.name', labelFallback: 'Name' },
  { key: 'legalName', labelKey: 'settings.companyInfo.field.legalName', labelFallback: 'Legal name' },
  { key: 'trnNumber', labelKey: 'settings.companyInfo.field.trn', labelFallback: 'TRN' },
  { key: 'phone', labelKey: 'settings.companyInfo.field.phone', labelFallback: 'Phone' },
];

/**
 * Company info — one card of the "Company" settings tab, and NOT its own form.
 *
 * IT HAS NO SAVE BUTTON, and that is the fix. It used to carry one, because it
 * writes `PATCH /settings/info` (the company record) while the page's foot bar
 * writes `PATCH /settings/:category` (the catalogue JSON) — two resources, so
 * the code concluded two buttons. The foot bar was already sending one request
 * per category in a single batch, and `/settings/info` accepts a partial body,
 * so the second resource is one more request in that batch and not a second
 * save. The draft lives in `useCompanyDraft`, which `SettingsPage` holds; this
 * component asks it for values and hands back keystrokes.
 *
 * The reader therefore has ONE Save on this screen, it is the page's, and the
 * count in the bar includes these fields — so an edit made here and then
 * navigated away from is still visible as unsaved from every other tab.
 *
 * `/settings/company` used to be this content's own route and is now a redirect
 * to `/settings/g/company` (see App.tsx).
 */
export function CompanyInfoSection({ draft }: { draft: CompanyDraft }): JSX.Element {
  const { t } = useTranslation();
  const { values } = draft;

  /* SIZED TO WHAT IT HOLDS (§6). This was `rows={3}` across BOTH columns — a
     720px-wide, ~90px slab holding "Sharjah Office" — which is a promise about
     how much is expected, and the promise was false. Two rows is the floor a
     multi-line address needs, and the box then MEASURES itself against its own
     content instead of standing at a fixed height: one line looks like one
     line, and a four-line address is not scrolled inside a two-line window.
     Height is the one property `rows` cannot express and no token owns; it is
     read off the content, never written as a design value. */
  const addressRef = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const el = addressRef.current;
    if (!el) return;
    el.style.height = 'auto';
    // An environment that does not lay out (jsdom, where every test of this
    // runs) reports 0 — leave `rows` to decide the height rather than writing a
    // zero-height box. Same guard as `TimeField`'s `scrollIntoView`.
    if (el.scrollHeight > 0) el.style.height = `${el.scrollHeight}px`;
  }, [values.address]);

  if (draft.isError) {
    return (
      <Card>
        <ErrorState
          title={t('settings.company.loadError.title', "Couldn't load company info")}
          description={
            friendlyMessage(draft.loadError) ||
            t(
              'common.checkConnectionUnchanged',
              'Check your connection and try again. Nothing has been changed.'
            )
          }
          action={
            <Button variant="outline" onClick={() => draft.refetch()}>
              {t('settings.company.loadError.retry', 'Try again')}
            </Button>
          }
        />
      </Card>
    );
  }

  // The record is not a blank slate — it is a complete, saveable company — so
  // rendering empty boxes while it is unread invites an overwrite of values
  // nobody has seen. The fields wait for the record.
  if (!draft.ready) {
    return (
      <Card>
        <Loading label={t('settings.company.loading', 'Loading company info…')} />
      </Card>
    );
  }

  const nameMissing = draft.obstacle === 'name';
  const currencyInvalid = draft.obstacle === 'currency';

  return (
    /* THE CARD OWNS ITS NAME AND ITS FIELDS — not a save.

       THE MEASURE IS ON THE FIELDS, NOT ON THE BOX. `max-w-form` (720px) used
       to wrap the whole section, from when this was its own routed page; beside
       `BrandingSection` and the group's full-width sections it made this card
       stop ~900px short of its siblings' right edge. §4b's reading column is
       about the LINE LENGTH a label and its input occupy, so it belongs on the
       field grid. */
    <section
      id="settings-section-companyInfo"
      aria-labelledby="settings-section-companyInfo-title"
    >
      <Card>
        <CardHeader>
          <CardTitle id="settings-section-companyInfo-title">
            {t('settings.s.companyInfo.title', 'Company info')}
          </CardTitle>
        </CardHeader>
        <CardContent className="grid max-w-form grid-cols-1 gap-component sm:grid-cols-2">
          {TEXT_FIELDS.map(({ key, labelKey, labelFallback }) => {
            const isName = key === 'name';
            const showNameError = isName && nameMissing;
            return (
              <div key={key} className="space-y-element">
                <Label htmlFor={`company-${key}`}>
                  {t(labelKey, labelFallback)}
                  {/* `RequiredMark`, not a hand-rolled asterisk span. §4b names
                      it: the marker already exists as a primitive, so the
                      logical-side spacing and the `aria-hidden` that keeps a
                      reader from announcing a stray "star" are decided once
                      rather than re-derived per form. `aria-required` on the
                      control below is what actually conveys "required". */}
                  {isName && <RequiredMark />}
                </Label>
                <Input
                  id={`company-${key}`}
                  type="text"
                  required={isName}
                  aria-required={isName || undefined}
                  // `error` derives `aria-invalid` AND the danger border; the
                  // describedby is what says WHY it is invalid.
                  error={showNameError}
                  aria-describedby={showNameError ? `company-${key}-error` : undefined}
                  value={values[key]}
                  onChange={(e) => draft.setValue(key, e.target.value)}
                />
                {showNameError && (
                  <p
                    id={`company-${key}-error`}
                    className="text-copy-sm text-danger"
                    role="alert"
                  >
                    {t('settings.companyInfo.nameRequired', 'Company name is required.')}
                  </p>
                )}
              </div>
            );
          })}

          <div className="space-y-element">
            <Label htmlFor="company-address">
              {t('settings.companyInfo.field.address', 'Address')}
            </Label>
            <Textarea
              id="company-address"
              ref={addressRef}
              rows={2}
              // The box grows with what is typed, so it never shows a scrollbar
              // inside two lines.
              className="overflow-hidden"
              value={values.address}
              onChange={(e) => draft.setValue('address', e.target.value)}
            />
          </div>

          {/* CURRENCY. Editable here because nothing else in the product edits
              it, and it is printed on every payslip line and every letter. The
              obstacle is named beside the field, the way the company name's is,
              rather than leaving a Save greyed out with no explanation. */}
          <div className="space-y-element">
            <Label htmlFor="company-currency">
              {t('settings.company.currency', 'Currency')}
              <RequiredMark />
            </Label>
            <Input
              id="company-currency"
              type="text"
              required
              aria-required
              maxLength={3}
              // The value is upper-cased on save; showing it upper-cased while
              // typing keeps the box and the payslip agreeing.
              className="uppercase"
              error={currencyInvalid}
              aria-describedby={
                currencyInvalid ? 'company-currency-error' : 'company-currency-hint'
              }
              value={values.currency}
              onChange={(e) => draft.setValue('currency', e.target.value)}
            />
            {currencyInvalid ? (
              <p id="company-currency-error" className="text-copy-sm text-danger" role="alert">
                {t(
                  'settings.company.currencyInvalid',
                  'Use a three-letter currency code, like AED.'
                )}
              </p>
            ) : (
              <p id="company-currency-hint" className="text-copy-sm text-muted-foreground">
                {t(
                  'settings.company.currencyHint',
                  'Printed beside every figure on payslips and letters.'
                )}
              </p>
            )}
          </div>

          {/* CANONICAL ELSEWHERE. `general.timezone` is catalogued under the PAY
              group (section `pay.day`), not under a "General" tab — the working
              day is what a timezone decides — and the link is built from that
              group's id so retiring or moving the field breaks the test rather
              than the reader's next click. */}
          <div className="space-y-element">
            <Label htmlFor="company-timezone">
              {t('settings.companyInfo.field.timezone', 'Timezone')}
            </Label>
            {/* `readOnly`, NOT `disabled`. This carries a real value an admin
                may want to read out or copy, and `disabled` takes the field out
                of the tab order and out of form submission — so a keyboard user
                could never reach it and could never see the "Managed in …" line
                that explains why it is fixed. `aria-readonly` states it,
                `aria-describedby` points at the explanation, and the muted skin
                keeps it looking non-editable (§3.11: the control names its
                obstacle rather than merely being grey). */}
            <Input
              id="company-timezone"
              value={draft.timezone}
              readOnly
              aria-readonly
              aria-describedby="company-timezone-managed"
              className="bg-muted text-muted-foreground"
            />
            {/* A LINK, not a sentence naming a screen. "Managed in Settings →
                General" named a tab that does not exist, so the one instruction
                the read-only field gives could not be followed. */}
            <p id="company-timezone-managed" className="text-copy-sm text-muted-foreground">
              <Link to="/settings/g/pay" className="underline">
                {t('settings.company.managedIn', 'Managed in {{screen}}', {
                  screen: t(
                    'settings.companyInfo.managedIn.payRules',
                    'Settings → Pay rules'
                  ),
                })}
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </section>
  );
}
